import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_BASE } from '../../utils/hosts';
import { DownloadIcon, PlusIcon, Trash2Icon, UploadIcon, WandSparklesIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Panel } from '../ui/Panel';
import { useAdmin } from '../AdminContext';
import type { RedirectsState } from './useRedirects';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnGhost, btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { mergeRedirects, normalizePath, normalizeTarget, parseRedirectsCsv, redirectsCsv, wooSuggestions } from '../../core/rules/redirects';
import { dateOf, fromIso, longDate, timeOf, today } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { downloadFile } from '../../utils/download';

/**
 * Instellingen › Vindbaarheid & pers: oude adressen doorverwijzen (301) en de stand van de sitemap. De
 * doorverwijzingen komen van de pagina (useRedirects in Settings) en gaan mee met de Opslaan-balk.
 */
export function SeoSettings({
  redirects: r
}: {
  redirects: RedirectsState;
}) {
  const {
    catalog
  } = useAdmin();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const suggestions = r.data ? wooSuggestions(catalog.products, r.items, today()) : [];
  const add = (e: FormEvent) => {
    e.preventDefault();
    const f = normalizePath(from);
    const t = normalizeTarget(to);
    if (!f) return setFormError('Het oude adres moet een pad op beautybewust.nl zijn, bijv. /product/serum.');
    if (!t) return setFormError('Het nieuwe adres moet een pad op de site zijn, of een volledige https-link.');
    setFormError(null);
    r.edit(list => mergeRedirects(list, [{
      from: f,
      to: t,
      createdAt: today()
    }]));
    setFrom('');
    setTo('');
  };
  const importCsv = async (file?: File) => {
    if (!file) return;
    const {
      items,
      rejected
    } = parseRedirectsCsv(await file.text(), today());
    if (items.length) r.edit(list => mergeRedirects(list, items));
    toast(items.length ? `${plural(items.length, 'regel', 'regels')} ingelezen` : 'Niets ingelezen', {
      description: rejected.length ? `${plural(rejected.length, 'regel', 'regels')} overgeslagen (regel ${rejected.slice(0, 5).map(x => x.line).join(', ')}). Twee kolommen: oud;nieuw.` : 'Controleer de lijst en sla op.'
    });
    if (fileRef.current) fileRef.current.value = '';
  };
  const exportCsv = () => downloadFile(redirectsCsv(r.items), 'doorverwijzingen.csv', 'text/csv;charset=utf-8');
  if (r.status === 'error' && !r.data) {
    return <Panel title="Vindbaarheid" data-id="element-4285" data-mp="gm2idftayf60">
        <LoadFailed variant="inline" title="De doorverwijzingen konden niet laden" onRetry={r.reload} data-id="element-4286" data-mp="glap4bn6gtiw" />
      </Panel>;
  }
  if (!r.data) return <LoadingLine label="Doorverwijzingen laden…" className="py-8 text-center" data-id="element-4287" data-mp="31dvys44fmnvt" />;
  return <>
      <Panel title="Sitemap" description="De lijst met pagina's die Google leest. De server maakt hem elke nacht en na elke wijziging opnieuw." data-id="element-4288" data-mp="2svsvxomliw8h">
        <p className="text-[14px] text-ink" data-id="element-4289" data-mp="2ouh3zcg91yx4">
          <span className="font-display text-3xl font-light tabular-nums" data-id="element-4290" data-mp="32u9ub6nv6d4k">{r.data.sitemap.pages}</span> pagina's
        </p>
        <p className="mt-1 text-[13px] text-muted" data-id="element-4291" data-mp="2xwgls6z39714">
          Laatst bijgewerkt {longDate(fromIso(dateOf(r.data.sitemap.updatedAt)))} om {timeOf(r.data.sitemap.updatedAt)} ·{' '}
          <a href="https://beautybewust.nl/sitemap.xml" target="_blank" rel="noreferrer" className="text-ink underline underline-offset-2" data-id="element-4292" data-mp="2579otcfa8phz">
            sitemap.xml
          </a>
        </p>
        {/* SEO staat op twee plekken; zo vind je de andere helft. */}
        <p className="mt-4 border-t border-line pt-3 text-[13px] text-muted" data-id="element-4293" data-mp="2f2um4x9ep1le">
          Titel en omschrijving in Google per pagina pas je aan in{' '}
          <Link to={`${ADMIN_BASE}/teksten`} className="text-ink underline underline-offset-2" data-id="element-4294" data-mp="1mh1pz6im8l7l">
            Paginateksten
          </Link>
          ; per product, behandeling of artikel in de editor daarvan. Welke pagina's weinig geklikt worden, zie je in{' '}
          <Link to={`${ADMIN_BASE}/inzicht?tab=bereik&bereik=google`} className="text-ink underline underline-offset-2" data-id="element-4295" data-mp="ullr7iqnjapp">
            Inzicht › Bereik
          </Link>
          .
        </p>
      </Panel>

      <Panel flush title="Doorverwijzingen" description="Een oude link (uit Google, een bladwijzer of Instagram) gaat zo naar de juiste nieuwe pagina, en houdt zijn plek in Google." actions={<span className="text-[13px] tabular-nums text-muted" data-id="element-4297" data-mp="1id0upeu5zy2j">{r.items.length}</span>} data-id="element-4296" data-mp="18fcx8h56ccgo">
        {suggestions.length > 0 && <div className="flex flex-wrap items-center gap-3 border-b border-line bg-sand/60 px-4 py-3 sm:px-5" data-id="element-4298" data-mp="1mduk6cn99kxj">
            <p className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-4299" data-mp="3k6i2qjm7j0xt">
              {plural(suggestions.length, 'product', 'producten')} uit de oude WooCommerce-winkel {suggestions.length === 1 ? 'heeft' : 'hebben'} nog geen doorverwijzing.
            </p>
            <button type="button" onClick={() => r.edit(list => mergeRedirects(list, suggestions))} className={btnSecondary} data-id="element-4300" data-mp="3bvre8813wf4v">
              <WandSparklesIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4301" data-mp="36xznhhc76z29" />
              Voeg ze toe
            </button>
          </div>}

        <form onSubmit={add} className="grid gap-2 border-b border-line px-4 py-4 sm:grid-cols-[1fr_1fr_auto] sm:px-5" aria-describedby={formError ? 'redirect-error' : undefined} data-id="element-4302" data-mp="3g7nbfnwr8sz">
          <label data-id="element-4303" data-mp="3uky2eako5yy2">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4304" data-mp="3qkxbvrkapxan">Oud adres</span>
            <input value={from} onChange={e => setFrom(e.target.value)} placeholder="/product/oud-serum" className={inputCls} autoCapitalize="off" spellCheck={false} data-id="element-4305" data-mp="3qn07qs84qkna" />
          </label>
          <label data-id="element-4306" data-mp="2f3xkqz9opoi7">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4307" data-mp="2a6xean8xajqk">Nieuw adres</span>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="/webshop/serum" className={inputCls} autoCapitalize="off" spellCheck={false} data-id="element-4308" data-mp="2a90axq4kzb49" />
          </label>
          <button type="submit" className={`${btnSecondary} self-end`} data-id="element-4309" data-mp="2gmnv463kv7zs">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4310" data-mp="22md1ivavvfax" />
            Toevoegen
          </button>
          {formError && <p id="redirect-error" role="alert" className="text-[12px] text-clay sm:col-span-3" data-id="element-4311" data-mp="26qukwtdbzql6">
              {formError}
            </p>}
        </form>

        {r.items.length === 0 ? <p className="px-5 py-8 text-center text-[13px] text-muted" data-id="element-4312" data-mp="1xq4t00r6ykcq">Nog geen doorverwijzingen.</p> : <div className="overflow-x-auto" data-id="element-4313" data-mp="1ny8s6hxq0g4t">
            <table className="w-full text-left text-[13px]" data-id="element-4314" data-mp="1nza4squdxaon">
              <thead className="text-[12px] text-muted" data-id="element-4315" data-mp="19y7eu309rg8g">
                <tr className="border-b border-line" data-id="element-4316" data-mp="19zir6r7s5wb6">
                  <th scope="col" className="px-4 py-2 font-normal sm:px-5" data-id="element-4317" data-mp="1ey3lka0cplrp">Oud</th>
                  <th scope="col" className="px-2 py-2 font-normal" data-id="element-4318" data-mp="vbiha6pkvd4p">Nieuw</th>
                  <th scope="col" className="sr-only" data-id="element-4319" data-mp="vdv693tr6nr9">Verwijderen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-4320" data-mp="14cgo0wrehyqz">
                {r.items.map((item, i) => <tr key={item.from} className="align-top" data-id="element-4321" data-mp="10bd6m5tpfunn" data-mpi={i}>
                    <td className="max-w-[16rem] break-all px-4 py-3 font-mono text-ink sm:px-5" data-id="element-4322" data-mp="109triijsbpng">{item.from}</td>
                    <td className="max-w-[18rem] px-2 py-3" data-id="element-4323" data-mp="1e8joly6ltpbt">
                      <span className="break-all font-mono text-ink" data-id="element-4324" data-mp="19b0cp4xdpon1">{item.to}</span>
                      {r.issues[i].map(issue => <span key={issue.text} className={`mt-1 block text-[12px] ${issue.level === 'error' ? 'text-clay' : 'text-muted'}`} data-id="element-4325" data-mp="1o2iloirtmiww">
                          {issue.level === 'error' ? 'Fout: ' : 'Let op: '}
                          {issue.text}
                        </span>)}
                    </td>
                    <td className="w-12 py-2 pr-3 text-right" data-id="element-4326" data-mp="2zfjl5y3a1827">
                      <button type="button" onClick={() => r.edit(list => list.filter(x => x.from !== item.from))} className={btnIcon} aria-label={`Doorverwijzing van ${item.from} verwijderen`} data-id="element-4327" data-mp="2zh309ld75d2e">
                        <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4328" data-mp="38dlvj48whd25" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}

        <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 sm:px-5" data-id="element-4329" data-mp="2kqnf26ks6548">
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="sr-only" id="redirect-csv" onChange={e => importCsv(e.target.files?.[0])} data-id="element-4330" data-mp="2po5ff8kqfc0m" />
          <label htmlFor="redirect-csv" className={`${btnGhost} cursor-pointer`} data-id="element-4331" data-mp="1x0t45uk0uqmm">
            <UploadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4332" data-mp="21zcq3uym82ez" />
            CSV importeren
          </label>
          <button type="button" onClick={exportCsv} disabled={!r.items.length} className={btnGhost} data-id="element-4333" data-mp="1ug3hi3xxo7qz">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4334" data-mp="1pgh5fxtucxjr" />
            Exporteren
          </button>
          {r.dirty && <p role="status" className={`ml-auto text-[12px] ${r.errors > 0 ? 'text-clay' : 'text-muted'}`} data-id="element-4335" data-mp="1kjic3hi04umm">
              {r.errors > 0 ? `Nog ${plural(r.errors, 'fout', 'fouten')}: los ze op voor je opslaat` : 'Gaat mee met Opslaan'}
            </p>}
        </div>
      </Panel>
    </>;
}